import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getIngredient } from '../api/endpoints';
import type { Ingredient } from '../types/api';
import { getErrorMessage } from '../utils/errors';

const SZ = { width: 18, height: 18 } as const;

const IconBack = () => (
    <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
    </svg>
);

const IconCheck = () => (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const IconMinus = () => (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
    </svg>
);

export default function IngredientDetailsPage(): React.JSX.Element {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [ingredient, setIngredient] = useState<Ingredient | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchIng = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const data = await getIngredient(Number.parseInt(id));
                setIngredient(data);
            } catch (error_: unknown) {
                setError(getErrorMessage(error_, 'Failed to load ingredient details'));
            } finally {
                setLoading(false);
            }
        };
        void fetchIng();
    }, [id]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <div style={{
                    width: 'var(--s-7)',
                    height: 'var(--s-7)',
                    borderRadius: '50%',
                    border: '2px solid var(--c-teal-weak)',
                    borderTopColor: 'var(--c-kivo-teal)',
                    animation: 'kvSpin 0.7s linear infinite',
                }} />
            </div>
        );
    }

    if (error || !ingredient) {
        return (
            <div style={{ paddingTop: 'var(--s-10)' }}>
                <div className="stack-4" style={{ maxWidth: 480, marginInline: 'auto' }}>
                    <div className="notice notice--error">{error ?? 'Ingredient not found'}</div>
                    <button onClick={() => { navigate('/ingredients'); }} className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }}>
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="stack-6" style={{ paddingTop: 'var(--s-5)', paddingBottom: 'var(--s-10)' }}>
            {/* Back nav */}
            <button
                onClick={() => { navigate('/ingredients'); }}
                className="btn btn--ghost btn--sm"
                style={{ alignSelf: 'flex-start' }}
            >
                <IconBack />
                Ingredient database
            </button>

            {/* Hero card */}
            <div className="card card--xl card--bordered scan-pattern" style={{ padding: 'clamp(var(--s-4), 4vw, var(--s-7))' }}>
                <div style={{ display: 'flex', gap: 'clamp(var(--s-4), 4vw, var(--s-7))', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {/* Identity */}
                    <div className="stack-4" style={{ flex: '1 1 200px', minWidth: 0 }}>
                        <span className="t-label">INCI profile</span>
                        <h1 style={{ margin: 0, font: `500 clamp(24px, 6.5vw, 44px)/1.10 var(--f-display)`, letterSpacing: '-0.01em', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                            {ingredient.normalized_name}
                        </h1>
                        <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span className="pill pill--teal">INCI: {ingredient.inci_name}</span>
                            {ingredient.cosing_id && (
                                <span className="pill">ID: {ingredient.cosing_id}</span>
                            )}
                        </div>

                        {ingredient.alt_names.length > 0 && (
                            <div className="card card--sand card--flat stack-2" style={{ padding: 'var(--s-3) var(--s-4)' }}>
                                <span className="t-label">Also known as</span>
                                <span className="t-small t-mute">{ingredient.alt_names.join(' · ')}</span>
                            </div>
                        )}
                    </div>

                    {/* Formulation flags */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--s-3)',
                        flex: '0 0 auto',
                        width: 'min(280px, 100%)',
                    }}>
                        <FlagCard active={ingredient.preservative} label="Preservative" />
                        <FlagCard active={ingredient.fragrance} label="Fragrance" />
                        <FlagCard active={ingredient.contains_ethanol} label="Ethanol" />
                        <FlagCard active={!ingredient.animal_derived} label="Non-animal" />
                    </div>
                </div>
            </div>

            {/* Main + aside grid */}
            <div className="g-content-aside">
                {/* Left: main content */}
                <div className="stack-6">
                    {/* Cosmetic functions */}
                    <div className="card card--bordered stack-4">
                        <span className="t-label">Cosmetic functions</span>
                        {ingredient.functions.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                                {ingredient.functions.map(f => (
                                    <span key={f} className="pill">{f}</span>
                                ))}
                            </div>
                        ) : (
                            <p className="t-small t-mute t-italic" style={{ margin: 0 }}>
                                No functions documented.
                            </p>
                        )}
                    </div>

                    {/* Formulation profile */}
                    <div className="card card--sand card--bordered stack-4">
                        <span className="t-label">Formulation profile</span>
                        <p className="t-body t-mute" style={{ margin: 0 }}>
                            This ingredient serves as a key structural or functional component within
                            cosmetic chemistry. Based on its classified functions —{' '}
                            <span className="t-teal">
                                {ingredient.functions.slice(0, 2).join(' and ').toLowerCase() || 'formulation stability'}
                            </span>
                            {' '}— it actively contributes to the product&apos;s finish, spreadability,
                            or preservation.
                        </p>
                        <p className="t-small t-mute" style={{ margin: 0 }}>
                            Its integration with other formulation elements is evaluated to ensure
                            consistent aesthetic performance and extended shelf life without altering
                            the sensory profile.
                        </p>
                    </div>
                </div>

                {/* Right: sidebar */}
                <div>
                    <div className="card card--bordered stack-4">
                        <span className="t-label">Specification</span>
                        <table className="data-table">
                            <tbody>
                                <SpecRow
                                    label="Halal compatibility"
                                    value={
                                        ingredient.halal_risk === 'low' ? 'Compatible' :
                                        ingredient.halal_risk === 'high' ? 'Review needed' :
                                        'Standard'
                                    }
                                />
                                <SpecRow
                                    label="Regulatory status"
                                    value={ingredient.restriction_flag ? 'Restricted' : 'Standard use'}
                                />
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FlagCard({ active, label }: { active: boolean; label: string }) {
    return (
        <div className={`flag-card${active ? '' : ' flag-card--inactive'}`}>
            <div className={`flag-card__icon${active ? '' : ' flag-card__icon--inactive'}`}>
                {active ? <IconCheck /> : <IconMinus />}
            </div>
            <span className="flag-card__label">{label}</span>
        </div>
    );
}

function SpecRow({ label, value }: { label: string; value: string }) {
    return (
        <tr>
            <td style={{ color: 'var(--ink-muted)' }}>{label}</td>
            <td style={{ textAlign: 'right', fontWeight: 500 }}>{value}</td>
        </tr>
    );
}
